import * as Y from '../../../../shared/yjs';

export type ParagraphStyle = {
  id: string;
  name: string;
  // Font properties
  fontFamily?: string;
  fontSize?: number;
  lineHeight?: number;
  fontWeight?: string;
  // Spacing properties (vertical)
  marginTop?: number;
  marginBottom?: number;
  // Indentation properties (horizontal) - ABSOLUTE from page edge
  // These are stored as absolute positions and converted to relative during CSS generation
  // E.g., Character at 3.7" from page edge, regardless of page margins
  absoluteMarginLeft?: number;   // Position from left page edge (pt)
  absoluteMarginRight?: number;  // Position from right page edge (pt)
  textIndent?: number;
  defaultNumStyleId?: string | null;
  // Text formatting properties
  textAlign?: string;
  textDecoration?: string;
  textTransform?: string;
  // Style linkage & inheritance (future)
  listLink?: { listStyleId: string; level: number } | null;
  basedOn?: string | null;
  next?: string | null;
};

export type CharacterStyle = {
  id: string;
  name: string;
  fontFamily?: string;
  fontSize?: number;
  fontWeight?: string;
  letterSpacing?: number;
  color?: string;
  background?: string;
  smallCaps?: boolean;
};

export type ListLevelSpec = {
  pattern: string; // e.g. "%1.", "(a)", "%1.%2."
  start?: number;
  indent?: { left?: number; firstLine?: number };
  follow?: 'tab' | 'space' | 'nothing';
};

export type ListStyle = {
  id: string;
  name?: string;
  levels: ListLevelSpec[];
};

export type DocDefaults = {
  run?: { fontFamily?: string; size?: number; bold?: boolean; color?: string };
  para?: { spacing?: any; indent?: any; align?: string };
};

export type ThemeSpec = {
  fonts?: { major?: string; minor?: string };
  colors?: Record<string, string>;
};

export type StyleRegistry = {
  paragraph: Record<string, ParagraphStyle>;
  character: Record<string, CharacterStyle>;
  list?: Record<string, ListStyle>;
  docDefaults?: DocDefaults;
  theme?: ThemeSpec;
};

const MAP_KEY = 'style:registry';

export const StyleService = {
  getMap(ydoc?: Y.Doc | null): Y.Map<any> | null {
    if (!ydoc) return null;
    return ydoc.getMap(MAP_KEY) as Y.Map<any>;
  },
  read(ydoc?: Y.Doc | null): StyleRegistry {
    const map = this.getMap(ydoc);
    if (!map) return { paragraph: {}, character: {} } as StyleRegistry;
    const json = (typeof (map as any).toJSON === 'function') ? (map as any).toJSON() : {};
    return {
      paragraph: json.paragraph || {},
      character: json.character || {},
      list: json.list || {},
      docDefaults: json.docDefaults || { run: {}, para: {} },
      theme: json.theme || { fonts: {}, colors: {} },
    } as StyleRegistry;
  },
  upsertParagraphStyle(ydoc: Y.Doc | null, style: ParagraphStyle) {
    const map = this.getMap(ydoc); if (!map) return;
    const reg = this.read(ydoc);
    reg.paragraph[style.id] = style;
    map.set('paragraph', reg.paragraph);
  },
  upsertCharacterStyle(ydoc: Y.Doc | null, style: CharacterStyle) {
    const map = this.getMap(ydoc); if (!map) return;
    const reg = this.read(ydoc);
    reg.character[style.id] = style;
    map.set('character', reg.character);
  },
  upsertListStyle(ydoc: Y.Doc | null, style: ListStyle) {
    const map = this.getMap(ydoc); if (!map) return;
    const reg = this.read(ydoc);
    const lists = { ...(reg.list || {}) } as Record<string, ListStyle>;
    lists[style.id] = style;
    map.set('list', lists);
  },
  setDocDefaults(ydoc: Y.Doc | null, defaults: DocDefaults) {
    const map = this.getMap(ydoc); if (!map) return;
    map.set('docDefaults', defaults || {});
  },
  setTheme(ydoc: Y.Doc | null, theme: ThemeSpec) {
    const map = this.getMap(ydoc); if (!map) return;
    map.set('theme', theme || {});
  },
  removeStyle(ydoc: Y.Doc | null, kind: 'paragraph' | 'character' | 'list', id: string) {
    const map = this.getMap(ydoc); if (!map) return;
    const reg = this.read(ydoc);
    if (kind === 'list') {
      const lists = { ...(reg.list || {}) } as Record<string, ListStyle>;
      delete lists[id];
      map.set('list', lists);
      return;
    }
    delete (reg as any)[kind][id];
    map.set(kind, (reg as any)[kind]);
  },
};


